// ----------------------------------------------------------------------
//
// Visit planning (#613). The counsellor says when they should next see a
// student; the student's visit status is worked out from that day, never
// stored. The day is a calendar day ('YYYY-MM-DD'), like to-do due dates, so
// time zones never shift it.
//
// Kept identical in server/services/career-visits/visit-status.js and
// src/sections/career/visit-status.js (the CRA app can't import from outside
// src/); server/constants.sync.test.js fails if they drift.
//
// ----------------------------------------------------------------------

const VISIT_STATUS = {
  OVERDUE: 'overdue',
  DUE_SOON: 'dueSoon',
  PLANNED: 'planned',
  NO_FOLLOW_UP: 'noFollowUp',
  NO_PLAN: 'noPlan',
};

// A visit planned within this many days is "due soon"
const DUE_SOON_DAYS = 14;

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar day written 'YYYY-MM-DD'. */
const isDay = (value) => {
  const match = typeof value === 'string' && DAY.exec(value);
  if (!match) return false;
  return new Date(Date.UTC(+match[1], +match[2] - 1, +match[3])).toISOString().slice(0, 10) === value;
};

/** The day `n` days after `day` (before, when negative). */
const addDays = (day, n) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** Whole days from `from` to `to` (negative when `to` is earlier). */
const daysBetween = (from, to) => {
  const ms = (day) => {
    const [y, m, d] = day.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((ms(to) - ms(from)) / 86400000);
};

/** The Monday of the week `day` falls in. */
const mondayOf = (day) => {
  const [y, m, d] = day.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 is Sunday
  return addDays(day, -((weekday + 6) % 7));
};

/**
 * The student's visit status from their `nextVisit` ({ by, noFollowUp }) and
 * today's day. No plan: no date set (never visited, or never planned).
 */
const visitStatus = (nextVisit, today) => {
  if (nextVisit?.noFollowUp) return VISIT_STATUS.NO_FOLLOW_UP;
  const by = nextVisit?.by;
  if (!isDay(by)) return VISIT_STATUS.NO_PLAN;
  if (by < today) return VISIT_STATUS.OVERDUE;
  if (by <= addDays(today, DUE_SOON_DAYS)) return VISIT_STATUS.DUE_SOON;
  return VISIT_STATUS.PLANNED;
};

/**
 * The last day of the school year `today` is in: June 30. From September on,
 * that is next year's; July and August still belong to the year just ended.
 */
const schoolYearEnd = (today) => {
  const [y, m] = today.split('-').map(Number);
  return `${m >= 9 ? y + 1 : y}-06-30`;
};

/** A Grade 12 student is finished once the school year has ended. */
const hasGraduated = (grade, today) => Number(grade) === 12 && today > schoolYearEnd(today);

/** The latest day a student's next visit may be set to: graduation, for Grade 12; no limit otherwise. */
const latestNextVisit = (grade, today) => (Number(grade) === 12 ? schoolYearEnd(today) : null);

module.exports = {
  VISIT_STATUS,
  DUE_SOON_DAYS,
  isDay,
  addDays,
  daysBetween,
  mondayOf,
  visitStatus,
  schoolYearEnd,
  hasGraduated,
  latestNextVisit,
};
